import { load } from 'js-yaml';
// The EXACT file the server loads fail-closed at boot (PermissionMatrix.RESOURCE). The SPA and the
// server build from the same commit and deploy together, so this is the server matrix, not a copy —
// there is no serving endpoint (the matrix is compile-time authority, not runtime data), and a
// hand-maintained duplicate would be the drift risk this import removes.
import matrixRaw from '../../../platform-security/src/main/resources/opsflow/permission-matrix.yaml?raw';

/**
 * Read-only view of the permission matrix (the SINGLE authorization authority) for the IAM console
 * (OPSFL-IAMUI-1). Parsing is strict where it matters (unknown shapes throw → the panel shows a
 * loud error, mirroring the server loader's refuse-on-drift stance) and the vitest suite parses the
 * REAL file so a format change breaks the build, never the rendered table silently.
 */

export interface MatrixKey {
  key: string;
  /** Resource group — the prefix before the first `.` (e.g. `flows`, `uam`, `commercial`). */
  resource: string;
  roles: string[];
  /** `mfa: STEP_UP` — the §6.1 elevation lanes (enforcement is a separate server flag). */
  stepUp: boolean;
}

export interface PermissionMatrixModel {
  schemaVersion: number;
  roles: string[];
  keys: MatrixKey[];
}

interface RawKeyBody {
  roles?: unknown;
  mfa?: unknown;
}

interface RawMatrix {
  schema_version?: unknown;
  roles?: unknown;
  keys?: unknown;
}

/** Parse a permission-matrix YAML document. Throws on structural drift (fail loud, never guess). */
export function parsePermissionMatrix(raw: string): PermissionMatrixModel {
  const doc = load(raw) as RawMatrix;
  if (!doc || typeof doc !== 'object') throw new Error('permission matrix: not a YAML mapping');
  const schemaVersion = typeof doc.schema_version === 'number' ? doc.schema_version : NaN;
  if (schemaVersion !== 1) {
    throw new Error(`permission matrix: unsupported schema_version ${String(doc.schema_version)}`);
  }
  if (!Array.isArray(doc.roles) || doc.roles.some((r) => typeof r !== 'string')) {
    throw new Error('permission matrix: `roles` must be a string list');
  }
  const roles = doc.roles as string[];
  if (!doc.keys || typeof doc.keys !== 'object' || Array.isArray(doc.keys)) {
    throw new Error('permission matrix: `keys` must be a mapping');
  }
  const roleSet = new Set(roles);
  const keys: MatrixKey[] = Object.entries(doc.keys as Record<string, RawKeyBody>).map(
    ([key, body]) => {
      if (!body || !Array.isArray(body.roles) || body.roles.some((r) => typeof r !== 'string')) {
        throw new Error(`permission matrix: key "${key}" has no valid \`roles\` list`);
      }
      const keyRoles = body.roles as string[];
      const unknown = keyRoles.filter((r) => !roleSet.has(r));
      if (unknown.length) {
        throw new Error(`permission matrix: key "${key}" grants unknown role(s) ${unknown.join(', ')}`);
      }
      return {
        key,
        resource: key.split('.')[0],
        roles: keyRoles,
        stepUp: body.mfa === 'STEP_UP',
      };
    },
  );
  return { schemaVersion, roles, keys };
}

let cached: PermissionMatrixModel | null = null;

/** The bundled matrix, parsed once. Throws on drift — callers catch and render the error loudly. */
export function getPermissionMatrix(): PermissionMatrixModel {
  if (!cached) cached = parsePermissionMatrix(matrixRaw);
  return cached;
}

/** Where a resolved matrix came from — the panel says so instead of implying a single source. */
export type MatrixSource = 'server' | 'bundle';

/**
 * Convert the served `GET /v1/uam/matrix` view into the panel model — strict like the YAML
 * parser (an unknown-role grant or a malformed key throws; the caller then falls back or
 * renders the loud error, never a wrong table).
 */
export function serverMatrixToModel(view: {
  schemaVersion: number;
  roles: string[];
  keys: { key: string; roles: string[]; stepUp: boolean }[];
}): PermissionMatrixModel {
  if (view.schemaVersion !== 1) {
    throw new Error(`permission matrix: unsupported schema_version ${String(view.schemaVersion)}`);
  }
  if (!Array.isArray(view.roles) || view.roles.some((r) => typeof r !== 'string')) {
    throw new Error('permission matrix: `roles` must be a string list');
  }
  const roleSet = new Set(view.roles);
  if (!Array.isArray(view.keys) || view.keys.length === 0) {
    throw new Error('permission matrix: `keys` must be a non-empty list');
  }
  const keys: MatrixKey[] = view.keys.map((k) => {
    if (!k || typeof k.key !== 'string' || !Array.isArray(k.roles)) {
      throw new Error('permission matrix: malformed served key');
    }
    const unknown = k.roles.filter((r) => !roleSet.has(r));
    if (unknown.length) {
      throw new Error(`permission matrix: key "${k.key}" grants unknown role(s) ${unknown.join(', ')}`);
    }
    return { key: k.key, resource: k.key.split('.')[0], roles: k.roles, stepUp: k.stepUp === true };
  });
  return { schemaVersion: view.schemaVersion, roles: view.roles, keys };
}

/**
 * SERVER-FIRST resolution (OPSFL-THIAM-1): prefer `GET /v1/uam/matrix` — the matrix the RUNNING
 * server enforces — and fall back to the same-commit bundled copy when the endpoint is absent
 * (older server, 404), refused (the caller holds no uam read, 403), or unreachable. The fallback
 * keeps the panel working against every deployed server; the source rides back so the panel can
 * say which one it rendered. A bundle parse failure still throws — refuse-to-guess is preserved.
 */
export async function resolvePermissionMatrix(
  fetchServer: () => Promise<{
    schemaVersion: number;
    roles: string[];
    keys: { key: string; roles: string[]; stepUp: boolean }[];
  }>,
): Promise<{ model: PermissionMatrixModel; source: MatrixSource }> {
  try {
    return { model: serverMatrixToModel(await fetchServer()), source: 'server' };
  } catch {
    return { model: getPermissionMatrix(), source: 'bundle' };
  }
}

/**
 * Plain-language blurbs per resource GROUP (kept intentionally at group grain: 80+ keys with
 * hand-written per-key prose would rot; the key's `.action` suffix already names the verb).
 * Unknown groups fall back to the key itself — never invent a description.
 */
export const RESOURCE_BLURBS: Readonly<Record<string, string>> = {
  flows: 'Workflow authoring & publishing (maker/checker split: author vs publish).',
  approvals: 'Maker-checker approvals for regulated-tier changes.',
  runs: 'Live workflow runs — read, start, signal.',
  bulk: 'Bulk CSV start-runs: upload, confirm, batch reports. Each start still needs runs.start on the flow.',
  entities: 'Entity-360 reads (PII floor: explicit grant only).',
  simulations: 'What-if simulation of a version before it goes live.',
  workitems: 'The investigator work queue — triage, assign, disposition.',
  rfi: 'Requests for information to end-customers.',
  migration: 'The legacy-workflow migration review queue.',
  decisions: 'Decision records & dossiers.',
  dossiers: 'Case dossiers (audit artifacts).',
  oversight: 'Hub-wide oversight roll-up across spokes.',
  metering: 'Metered usage reads (quantities, never prices).',
  templates: 'The flow template gallery.',
  spokes: 'Operating-unit (spoke) inventory & provisioning.',
  profile: 'The hub regime & compliance profile.',
  quotas: 'Capability allowlist, per-family quotas, flow-cost budget.',
  capabilities: 'Capability-fabric bindings & entitlement reads.',
  dataroom: 'End-customer data-room invites (mint, read, revoke).',
  uam: 'User access management — the people directory and their authority.',
  acl: 'Instance-level access control (grants, confinements, restrictions).',
  projects: 'Projects — non-isolating authority scopes over bodies of work.',
  roles: 'Tenant-authored custom roles (x_<slug>).',
  delegation: 'Bounded delegated administration.',
  abac: 'Attribute-based DENY policies.',
  federation: 'Identity federation (who may enter through the door).',
  subscriptions: 'Outbound webhooks — subscriptions and deliveries.',
  theme: 'White-label theming.',
  screens: 'Screen Studio — applicant-facing screen configs.',
  alerts: 'The alert centre — triage and routing rules.',
  cases: 'Case actions — propose an override, second-review one, run a step again, ask for a suggestion, and set the hub\'s kill switch and budget for suggestions. A suggestion changes nothing.',
  'api-clients': 'Machine clients (OAuth2 client credentials) for this hub.',
  'api-keys': 'Hub API keys for machine callers.',
  analytics: 'Operational analytics: read the metrics and KYC trends, save your own dashboard layout, and read API usage per machine credential.',
  audit: 'Export the hub\'s audit trail (decisions, approvals, document opens) as CSV or JSON. Bounded, erasure-aware, Aadhaar masked.',
  commercial: 'The commercial spine — contracts, rates, rating, licenses, invoicing.',
  platform: 'Platform plane (provider registry, tenant creation) — platform_admin only.',
};
